import CollapsePanel from "molecules/collapse-panel/collapse-panel";
import React, { useEffect } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import { DataTestAttributes } from "interfaces/data-test-attributes";
import StringUtils from "utilities/string-utils";
import ChapterTableOfContentsRecord from "models/view-models/table-of-contents/chapter-table-of-contents-record";
import { SectionParentType } from "utilities/enumerations/section-parent-type";
import PublicationComponentType from "utilities/enumerations/publication-component-type";
import NotebookTableOfContentsHeaderButton from "pages/notebooks/notebook-reference-panel/bookview/table-of-contents/notebook-table-of-contents-header-button";
import VirtualTableOfContentsSection from "molecules/virtual-table-of-contents-items/virtual-table-of-contents-section";
import VirtualTableOfContentsArticle from "molecules/virtual-table-of-contents-items/virtual-table-of-contents-article";
import { IconSizes } from "atoms/constants/icon-sizes";
import { useOpenTocPanels } from "pages/notebooks/notebook-reference-panel/use-open-toc-panels";

interface VirtualTableOfContentsChapterProps
    extends Pick<DataTestAttributes, "dataTestId"> {
    allCollapsed: boolean;
    chapter: ChapterTableOfContentsRecord;
    locale?: string;
    isPublic?: boolean;
    publicationId: number;
}

const CSS_CLASS_NAME = "c-virtual-table-of-contents";

const VirtualTableOfContentsChapter: React.FunctionComponent<
    VirtualTableOfContentsChapterProps
> = (props: VirtualTableOfContentsChapterProps) => {
    const { chapter, locale, publicationId } = props;
    const { isPanelCollapsed, openPanels } = useOpenTocPanels();

    const { articles, sections } = chapter;
    const displaySections = sections?.filter((s) => !s.isDeleted);

    const cssEmptyModifier = "-empty";
    const isEmpty =
        CollectionUtils.isEmpty(articles) &&
        CollectionUtils.isEmpty(displaySections);
    const cssClassName = StringUtils.joinClassNames([
        CSS_CLASS_NAME,
        isEmpty ? cssEmptyModifier : "",
    ]);

    // Scrolls to the open chapter panel on render as long as there's not also another
    // open panel (like an article) that would take precedence
    useEffect(() => {
        const scrollToChapterIfNoOpenArticle = () => {
            if (
                !isPanelCollapsed(
                    chapter.id,
                    PublicationComponentType.Chapter
                ) &&
                openPanels.length === 1
            ) {
                const chapterElement = document.getElementById(
                    `${chapter.id}_${PublicationComponentType.Chapter}`
                );
                chapterElement?.scrollIntoView({
                    block: "start",
                    container: "nearest",
                } as ScrollIntoViewOptions);
            }
        };

        scrollToChapterIfNoOpenArticle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            className={`${cssClassName} -chapters`}
            id={`${chapter.id}_${PublicationComponentType.Chapter}`}>
            <CollapsePanel
                collapse={isPanelCollapsed(
                    chapter.id,
                    PublicationComponentType.Chapter
                )}
                iconSize={IconSizes.Base}
                data-test-id="chapter-collapse-panel"
                panelTop={
                    <NotebookTableOfContentsHeaderButton
                        dataTestId={props.dataTestId}
                        dataTestDisplaySequence={chapter.displaySequence}
                        displayTitle={chapter.getDisplayTitle(locale)}
                        caapsId={chapter.id}
                        publicationComponentType={
                            PublicationComponentType.Chapter
                        }
                        parentId={chapter.id.toString()}
                    />
                }
                buttonAriaText={chapter.getDisplayTitle(locale)}
                keepAligned={true}>
                {CollectionUtils.hasValues(articles) && (
                    <VirtualTableOfContentsArticle
                        publicationId={publicationId}
                        articles={articles}
                        chapterNfpaLabel={chapter.nfpaLabel ?? ""}
                        parentId={chapter.id.toString()}
                    />
                )}
                {CollectionUtils.isEmpty(articles) &&
                    CollectionUtils.hasValues(displaySections) && (
                        <VirtualTableOfContentsSection
                            chapterNfpaLabel={chapter.nfpaLabel}
                            sections={displaySections}
                            useHashLinks={false}
                            parentType={SectionParentType.Chapter}
                        />
                    )}
            </CollapsePanel>
        </div>
    );
};

export default VirtualTableOfContentsChapter;
